// Screen wake lock — keeps the phone display awake during a battle. A tank
// that only turns and coasts can go many seconds without a touch, which is
// exactly when mobile screens dim and lock.
//
// Support notes (verified against MDN/caniuse as of 2026):
//   - Chrome/Edge Android: supported since Chrome 84.
//   - iOS Safari: supported since 16.4 (earlier iOS silently no-ops here).
//   - The UA auto-releases the lock whenever the page is hidden
//     (backgrounded, tab switch, screen off) — that is spec behavior, not a
//     bug — so we re-acquire on every return to visibility.

let sentinel: WakeLockSentinel | null = null;
let enabled = false;
let listenerAttached = false;

const supported = (): boolean =>
  typeof navigator !== 'undefined' && 'wakeLock' in navigator;

async function acquire(): Promise<void> {
  if (!enabled || !supported()) return;
  if (document.visibilityState !== 'visible') return;
  if (sentinel !== null && !sentinel.released) return;
  try {
    sentinel = await navigator.wakeLock.request('screen');
  } catch {
    // Request can reject (low-battery mode, permissions policy). Silent
    // no-op — the game must never depend on holding the lock.
    sentinel = null;
  }
}

/** Start holding a screen wake lock, re-acquiring it every time the page
 *  becomes visible again. Feature-detected; a silent no-op on browsers
 *  without the API (or on iOS < 16.4). Safe to call more than once. */
export function initWakeLock(): void {
  if (!supported()) return;
  enabled = true;
  if (!listenerAttached) {
    listenerAttached = true;
    document.addEventListener('visibilitychange', () => {
      void acquire();
    });
  }
  void acquire();
}

/** Release the lock and stop re-acquiring it (until initWakeLock is called
 *  again). Safe to call when no lock is held. */
export function releaseWakeLock(): void {
  enabled = false;
  const held = sentinel;
  sentinel = null;
  if (held !== null && !held.released) {
    held.release().catch(() => {
      // Already released by the UA — nothing to do.
    });
  }
}
